import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";

import PatientItemIdentityContext from "src/contexts/data/PatientItemIdentityContext";
import usePatientItemIdentityById from "src/hooks/data/load/usePatientItemIdentityById";

/**
 * Loads patient item identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RoutePatientItemIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { patientItemId } = useParams<{ patientItemId: string }>();
    const context = usePatientItemIdentityById(
        patientItemId ? Number.parseInt(patientItemId, 10) : undefined
    );

    return (
        <PatientItemIdentityContext.Provider value={context}>
            {children}
        </PatientItemIdentityContext.Provider>
    );
};
